'use client';

import clsx from 'clsx';
/* eslint-disable @next/next/no-html-link-for-pages -- R2 bundles cannot use next/link */
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    saveAssessmentProgress,
    loadAssessmentProgress,
    clearAssessmentProgress,
} from '@/lib/assessment-common/storage';
import type { SavedProgress } from '@/lib/assessment-common/storage';
import { getScoreColorClasses } from '@/lib/assessment-common/types';
import { roundScore } from '@/lib/assessment-common/scoring';
import { generateIRRAPDF } from './generateIRRAPDF';
import { LeadCaptureForm } from '@/components/calculator/LeadCaptureForm';

type ModuleId = 'protection' | 'performance' | 'validation' | 'saas';
type RiskLevel = 'critical' | 'high' | 'medium';

type Option = {
    value: string;
    label: string;
    score: number;
    helper?: string;
    detail?: string;
    hours?: number;
};

type QuestionId =
    | 'immutableCopy'
    | 'redundantMedia'
    | 'offsiteIsolation'
    | 'backupFrequency'
    | 'biaDefined'
    | 'tieredRecovery'
    | 'rpoCapability'
    | 'rtoTarget'
    | 'lastTestRto'
    | 'testFrequency'
    | 'execParticipation'
    | 'tabletopDepth'
    | 'usesSaaS'
    | 'saasBackup'
    | 'saasRetention'
    | 'saasKnowledge';

type Question = {
    id: QuestionId;
    module: ModuleId;
    prompt: string;
    helper?: string;
    type: 'single' | 'number';
    options?: Option[];
    risk: RiskLevel;
    dependsOn?: {
        id: QuestionId;
        values: string[];
    };
    remediation?: {
        title: string;
        action: string;
        link: {
            label: string;
            href: string;
        };
    };
    criticalMessage?: string;
    numberConfig?: {
        min?: number;
        max?: number;
        step?: number;
        suffix?: string;
        placeholder?: string;
    };
};

type Answers = Partial<Record<QuestionId, string | number>>;

type ModuleMeta = {
    title: string;
    description: string;
    icon: string;
};

type ModuleScore = {
    id: ModuleId;
    score: number | null;
    label: string;
};

type Alert = {
    id: QuestionId;
    message: string;
    severity: RiskLevel;
};

type MaturityBand = {
    min: number;
    max: number;
    label: string;
    description: string;
};

type RemediationItem = {
    questionId: QuestionId;
    title: string;
    action: string;
    link: {
        label: string;
        href: string;
    };
    severity: RiskLevel;
    score: number;
};

/**
 * Saved progress structure for IRRA
 * Extends shared SavedProgress interface
 */
interface IRRASavedProgress extends SavedProgress {
    answers: Answers;
    currentIndex: number;
    showResults: boolean;
    numberDraft?: string;
}

const moduleOrder: ModuleId[] = ['protection', 'performance', 'validation', 'saas'];

export type IRRAModuleScore = ModuleScore;
export type IRRAAlert = Alert;
export type IRRAMaturityBand = MaturityBand;
export type IRRARemediationItem = RemediationItem;

const moduleMeta: Record<ModuleId, ModuleMeta> = {
    protection: {
        title: 'Data Protection Principles',
        description: 'Validate your 3-2-1-1 architecture, immutability posture, and backup cadence.',
        icon: '🛡️',
    },
    performance: {
        title: 'Mission Criticality & Performance',
        description: 'Align RPO/RTO targets to business impact and quantify your true recovery tolerance.',
        icon: '⏱️',
    },
    validation: {
        title: 'Recovery Validation & IR Readiness',
        description: 'Measure testing cadence, executive involvement, and incident playbook maturity.',
        icon: '🚨',
    },
    saas: {
        title: 'SaaS & Cloud Resilience',
        description: 'Expose shared responsibility gaps across Microsoft 365, Google Workspace, and SaaS.',
        icon: '☁️',
    },
};

const riskWeights: Record<RiskLevel, number> = {
    critical: 0.5,
    high: 0.3,
    medium: 0.2,
};

const maturityBands: MaturityBand[] = [
    {
        min: 0,
        max: 20,
        label: 'Level 1: Unstructured',
        description: 'Backups are ad-hoc; RTO/RPO are undefined and unknown.',
    },
    {
        min: 21,
        max: 40,
        label: 'Level 2: Repeatable',
        description: 'Basic redundancy exists, but recovery objectives are rarely validated.',
    },
    {
        min: 41,
        max: 60,
        label: 'Level 3: Standardized',
        description: 'Automated 3-2-1 strategy in place; RTO/RPO defined, but immutability is missing.',
    },
    {
        min: 61,
        max: 80,
        label: 'Level 4: Managed',
        description: '3-2-1-1 enforced; quarterly tests validate RTO/RPO with leadership oversight.',
    },
    {
        min: 81,
        max: 100,
        label: 'Level 5: Optimized',
        description: 'Continuous recovery assurance with near-zero RTO/RPO for critical data.',
    },
];

const questionBank: Question[] = [
    {
        id: 'immutableCopy',
        module: 'protection',
        prompt: 'Do you maintain at least one backup copy that is immutable (cryptographically protected) or logically air-gapped?',
        helper: 'Immutability is the 1 in the 3-2-1-1 model. Without it, ransomware can encrypt every copy.',
        type: 'single',
        risk: 'critical',
        options: [
            { value: 'yes', label: 'Yes — immutable or air-gapped copy for every critical workload', score: 1 },
            { value: 'partial', label: 'Working toward immutability for select systems', score: 0.4 },
            { value: 'no', label: 'No — backups are writable or live within production networks', score: 0 },
        ],
        remediation: {
            title: 'No immutable backup tier',
            action: 'Implement object-lock or offline media so ransomware cannot modify or delete golden copies.',
            link: {
                label: 'Explore Backup & Ransomware Defense',
                href: '/services/backup-and-ransomware-defense',
            },
        },
        criticalMessage: 'Critical Alert: Your lack of an immutable or air-gapped copy fails the 3-2-1-1 standard and leaves backups susceptible to ransomware.',
    },
    {
        id: 'redundantMedia',
        module: 'protection',
        prompt: 'How many distinct media types host your backup copies?',
        helper: '3-2-1 expects multiple storage mediums (disk + object storage, tape, etc.) to survive localized failures.',
        type: 'single',
        risk: 'high',
        options: [
            { value: 'three', label: 'Three or more media types (disk, object, tape)', score: 1 },
            { value: 'two', label: 'Two media types (e.g., disk + cloud object storage)', score: 0.7 },
            { value: 'one', label: 'Single media type or same storage stack', score: 0.2 },
            { value: 'unknown', label: 'Unsure / depends on system', score: 0 },
        ],
        remediation: {
            title: 'Single media dependency',
            action: 'Introduce a secondary medium (object storage, tape, or immutable cloud tier) to meet 3-2-1 resiliency.',
            link: {
                label: 'Design a Tiered Backup Architecture',
                href: '/services/cloud-solutions',
            },
        },
    },
    {
        id: 'offsiteIsolation',
        module: 'protection',
        prompt: 'How isolated is your off-site copy from production credentials and networks?',
        type: 'single',
        risk: 'high',
        options: [
            { value: 'isolated', label: 'Dedicated account with MFA + network segmentation', score: 1 },
            { value: 'semi', label: 'Separate region but shared credentials or peering', score: 0.5 },
            { value: 'none', label: 'Backups reside in same security boundary as production', score: 0.1 },
        ],
        remediation: {
            title: 'Backup isolation gap',
            action: 'Segment backup control planes and credentials so attackers cannot traverse into recovery copies.',
            link: {
                label: 'Harden Backup Segmentation',
                href: '/services/small-business-cybersecurity/security-policies',
            },
        },
        criticalMessage: 'Critical Alert: Backups stored inside the same security boundary are easily encrypted alongside production systems.',
    },
    {
        id: 'backupFrequency',
        module: 'protection',
        prompt: 'How frequently are mission-critical systems backed up?',
        type: 'single',
        risk: 'medium',
        options: [
            { value: 'continuous', label: 'Continuous / near real-time replication', score: 1 },
            { value: 'hourly', label: 'Hourly snapshots or CDP', score: 0.8 },
            { value: 'daily', label: 'Daily backups', score: 0.5 },
            { value: 'weekly', label: 'Weekly or less frequent', score: 0.2 },
            { value: 'adHoc', label: 'Ad-hoc / not on a schedule', score: 0 },
        ],
        remediation: {
            title: 'Backup frequency lag',
            action: 'Increase backup cadence for Tier-1 workloads to minimize tolerable data loss.',
            link: {
                label: 'Modernize Backup Cadence',
                href: '/services/cloud-infrastructure-management',
            },
        },
    },
    {
        id: 'biaDefined',
        module: 'performance',
        prompt: 'Were RTO/RPO targets defined through a formal Business Impact Analysis with business stakeholders?',
        type: 'single',
        risk: 'high',
        options: [
            { value: 'yes', label: 'Yes — BIA completed with executive approval', score: 1 },
            { value: 'partial', label: 'Partially — informed by IT estimates, limited business input', score: 0.5 },
            { value: 'no', label: 'No — targets based on what technology can currently deliver', score: 0.1 },
            { value: 'unknown', label: 'Targets are not documented', score: 0 },
        ],
        remediation: {
            title: 'Missing business impact alignment',
            action: 'Run a business impact workshop to establish recovery objectives tied to financial and regulatory risk.',
            link: {
                label: 'Schedule a BIA & Continuity Review',
                href: '/services/compliance',
            },
        },
    },
    {
        id: 'tieredRecovery',
        module: 'performance',
        prompt: 'Do you map applications to tiered recovery groups with documented RTO and RPO targets?',
        type: 'single',
        risk: 'high',
        options: [
            { value: 'yes', label: 'Yes — tiered recovery catalog with owners and playbooks', score: 1 },
            { value: 'partial', label: 'Partially — catalog exists but lacks ownership or playbooks', score: 0.5 },
            { value: 'no', label: 'No — recovery is best effort across all systems', score: 0.1 },
        ],
        remediation: {
            title: 'Unprioritized recovery sequencing',
            action: 'Develop a tiered recovery plan so teams know which workloads must return first.',
            link: {
                label: 'Build Tiered Recovery Runbooks',
                href: '/services/incident-response',
            },
        },
    },
    {
        id: 'rpoCapability',
        module: 'performance',
        prompt: 'What is the current Recovery Point Objective (RPO) for your most critical systems?',
        type: 'single',
        risk: 'medium',
        options: [
            { value: 'fifteen', label: 'Under 15 minutes', score: 1 },
            { value: 'sixty', label: '60 minutes', score: 0.7 },
            { value: 'fourHours', label: '4 hours', score: 0.4 },
            { value: 'twentyFour', label: '24 hours', score: 0.2 },
            { value: 'unknown', label: 'Unknown or not defined', score: 0 },
        ],
        remediation: {
            title: 'RPO tolerance exceeded',
            action: 'Increase replication or journaling so mission-critical data loss stays within tolerance.',
            link: {
                label: 'Implement Continuous Data Protection',
                href: '/services/cloud-infrastructure-management',
            },
        },
    },
    {
        id: 'rtoTarget',
        module: 'performance',
        prompt: 'For your most critical system, what is the maximum acceptable downtime (RTO)?',
        helper: 'Targets should reflect business-defined tolerance, not existing technical limits.',
        type: 'single',
        risk: 'medium',
        options: [
            { value: '0.5', label: '30 minutes', score: 1, hours: 0.5 },
            { value: '1', label: '1 hour', score: 0.9, hours: 1 },
            { value: '4', label: '4 hours', score: 0.7, hours: 4 },
            { value: '8', label: '8 hours', score: 0.4, hours: 8 },
            { value: '24', label: '24 hours', score: 0.2, hours: 24 },
            { value: 'unknown', label: 'Unknown / not documented', score: 0 },
        ],
        remediation: {
            title: 'Undefined RTO benchmark',
            action: 'Document explicit downtime tolerances so technology plans align to economic impact.',
            link: {
                label: 'Facilitate a Recovery Target Workshop',
                href: '/services/vciso',
            },
        },
    },
    {
        id: 'lastTestRto',
        module: 'performance',
        prompt: 'During your last full recovery test, how long did it take to restore the system to production readiness?',
        helper: 'Enter the total hours from declaring an incident to verified system availability.',
        type: 'number',
        risk: 'critical',
        numberConfig: {
            min: 0,
            max: 168,
            step: 0.5,
            suffix: 'hours',
            placeholder: 'e.g., 6',
        },
        remediation: {
            title: 'RTO delta detected',
            action: 'Streamline failover runbooks and automation so real-world recovery meets target objectives.',
            link: {
                label: 'Request a Recovery Optimization Sprint',
                href: '/services/backup-and-ransomware-defense',
            },
        },
        criticalMessage: 'Critical Alert: Your tested recovery time exceeds the business-defined RTO target.',
    },
    {
        id: 'testFrequency',
        module: 'validation',
        prompt: 'How often do you perform full failover simulations for Tier-1 systems?',
        type: 'single',
        risk: 'critical',
        options: [
            { value: 'monthly', label: 'Monthly or more', score: 1 },
            { value: 'quarterly', label: 'Quarterly', score: 0.8 },
            { value: 'annually', label: 'Annually', score: 0.5 },
            { value: 'adHoc', label: 'Ad-hoc / less than annually', score: 0.2 },
            { value: 'never', label: 'Never performed a full failover', score: 0 },
        ],
        remediation: {
            title: 'Testing cadence gap',
            action: 'Institute a quarterly ransomware failover exercise with cross-team participation.',
            link: {
                label: 'Plan a Ransomware Playbook Exercise',
                href: '/services/incident-response',
            },
        },
        criticalMessage: 'Critical Alert: Without regular failover tests, you cannot prove recovery readiness.',
    },
    {
        id: 'execParticipation',
        module: 'validation',
        prompt: 'Do executives and business leaders participate in ransomware response tests at least annually?',
        type: 'single',
        risk: 'high',
        options: [
            { value: 'yes', label: 'Yes — executives co-lead scenarios and sign off on outcomes', score: 1 },
            { value: 'partial', label: 'Sometimes — limited executive visibility into testing', score: 0.5 },
            { value: 'no', label: 'No — exercises are confined to IT/operations', score: 0.1 },
        ],
        remediation: {
            title: 'Executive participation gap',
            action: 'Bring executives into ransomware tabletop exercises to align communications and decision authority.',
            link: {
                label: 'Schedule an Executive Tabletop',
                href: '/services/incident-response',
            },
        },
    },
    {
        id: 'tabletopDepth',
        module: 'validation',
        prompt: 'How mature is your documented ransomware incident response playbook?',
        type: 'single',
        risk: 'medium',
        options: [
            { value: 'integrated', label: 'Integrated with DR plans, with communications, legal, and forensics steps', score: 1 },
            { value: 'partial', label: 'Documented but missing cross-functional stakeholders', score: 0.5 },
            { value: 'basic', label: 'Basic checklist without tested escalation paths', score: 0.2 },
            { value: 'none', label: 'No ransomware-specific playbook', score: 0 },
        ],
        remediation: {
            title: 'Incident playbook gap',
            action: 'Author and socialize a ransomware-specific runbook that spans legal, communications, and IT.',
            link: {
                label: 'Build a Ransomware IR Playbook',
                href: '/services/incident-response',
            },
        },
    },
    {
        id: 'usesSaaS',
        module: 'saas',
        prompt: 'Do you rely on SaaS platforms (Microsoft 365, Google Workspace, Salesforce, etc.) for critical data?',
        type: 'single',
        risk: 'medium',
        options: [
            { value: 'yes', label: 'Yes — SaaS platforms host critical or regulated data', score: 1 },
            { value: 'partial', label: 'Partially — limited workloads rely on SaaS', score: 0.5 },
            { value: 'no', label: 'No — SaaS data is non-critical', score: 0.3 },
        ],
        remediation: {
            title: 'Unclear SaaS criticality',
            action: 'Audit SaaS usage to determine where critical records reside and document ownership.',
            link: {
                label: 'Run a SaaS Data Inventory',
                href: '/services/cloud-infrastructure-management',
            },
        },
    },
    {
        id: 'saasBackup',
        module: 'saas',
        prompt: 'Is SaaS data (e.g., Microsoft 365, Google Workspace) protected by a dedicated backup platform with isolated storage?',
        type: 'single',
        risk: 'critical',
        dependsOn: {
            id: 'usesSaaS',
            values: ['yes', 'partial'],
        },
        options: [
            { value: 'yes', label: 'Yes — dedicated backup with isolated storage and immutability', score: 1 },
            { value: 'inProgress', label: 'Piloting a SaaS backup solution', score: 0.4 },
            { value: 'no', label: 'No — relying on native retention only', score: 0 },
        ],
        remediation: {
            title: 'SaaS backup missing',
            action: 'Deploy third-party SaaS backup with isolated retention to close shared responsibility gaps.',
            link: {
                label: 'Protect Microsoft 365 & SaaS Data',
                href: '/services/backup-and-ransomware-defense',
            },
        },
        criticalMessage: 'Critical Alert: Native SaaS retention is not a backup. Dedicated SaaS backups are required for ransomware recovery and compliance.',
    },
    {
        id: 'saasRetention',
        module: 'saas',
        prompt: 'What is the guaranteed retention window for SaaS backups?',
        type: 'single',
        risk: 'high',
        dependsOn: {
            id: 'usesSaaS',
            values: ['yes', 'partial'],
        },
        options: [
            { value: 'seven', label: '7+ years with legal hold options', score: 1 },
            { value: 'three', label: '1-3 years', score: 0.6 },
            { value: 'native', label: 'Native retention only (30-180 days)', score: 0.2 },
            { value: 'unknown', label: 'Unknown', score: 0 },
        ],
        remediation: {
            title: 'Insufficient SaaS retention',
            action: 'Expand retention policies to meet legal and contract obligations for regulated data.',
            link: {
                label: 'Extend SaaS Retention Controls',
                href: '/services/compliance',
            },
        },
    },
    {
        id: 'saasKnowledge',
        module: 'saas',
        prompt: 'Which option ensures long-term (7+ year) retention for Microsoft 365 data?',
        helper: 'This diagnostic question exposes shared responsibility misconceptions.',
        type: 'single',
        risk: 'medium',
        dependsOn: {
            id: 'usesSaaS',
            values: ['yes', 'partial'],
        },
        options: [
            { value: 'native', label: 'Microsoft 365 native recycle bin and retention labels', score: 0.1 },
            { value: 'archiving', label: 'Archive mailbox licenses only', score: 0.3 },
            { value: 'backup', label: 'Dedicated SaaS backup with isolated storage', score: 1, helper: 'Correct' },
            { value: 'uncertain', label: 'Not sure', score: 0 },
        ],
        remediation: {
            title: 'Shared responsibility misconception',
            action: 'Educate stakeholders that Microsoft protects availability while customers protect data copies.',
            link: {
                label: 'Brief the Team on SaaS Shared Responsibility',
                href: '/services/small-business-cybersecurity/security-awareness',
            },
        },
    },
];

const initialAnswers = (): Answers => ({});

function buildQuestionSequence(answers: Answers) {
    return questionBank.filter((question) => isQuestionVisible(question, answers));
}

function isQuestionAnswered(question: Question, answers: Answers) {
    const value = answers[question.id];
    if (value === undefined) {
        return false;
    }
    if (question.type === 'number') {
        return typeof value === 'number' && !Number.isNaN(value);
    }
    return String(value).length > 0;
}

function isQuestionVisible(question: Question, answers: Answers) {
    if (!question.dependsOn) {
        return true;
    }
    const dependentAnswer = answers[question.dependsOn.id];
    if (dependentAnswer === undefined) {
        return false;
    }
    return question.dependsOn.values.includes(String(dependentAnswer));
}

function getOptionScore(question: Question, value: string | number | undefined) {
    if (!question.options) {
        return null;
    }
    const selected = question.options.find((option) => option.value === String(value));
    if (!selected) {
        return null;
    }
    return selected.score;
}

function computeNumberScore(question: Question, answers: Answers) {
    if (question.id !== 'lastTestRto') {
        return null;
    }
    const targetRaw = answers.rtoTarget;
    if (targetRaw === undefined || targetRaw === 'unknown') {
        // Without a target, we can only reward that testing occurred if value entered.
        const rawValue = answers.lastTestRto;
        if (rawValue === undefined || rawValue === '') {
            return null;
        }
        const numeric = typeof rawValue === 'number' ? rawValue : parseFloat(String(rawValue));
        if (Number.isNaN(numeric)) {
            return null;
        }
        // Encourage testing even without a target, but cap score to 0.4.
        return numeric >= 0 ? 0.4 : 0;
    }
    const rawValue = answers.lastTestRto;
    if (rawValue === undefined || rawValue === '') {
        return null;
    }
    const numeric = typeof rawValue === 'number' ? rawValue : parseFloat(String(rawValue));
    if (Number.isNaN(numeric)) {
        return null;
    }
    const targetOption = questionBank.find((q) => q.id === 'rtoTarget')?.options?.find((opt) => opt.value === String(targetRaw));
    const targetHours = targetOption?.hours;
    if (!targetHours && targetHours !== 0) {
        return null;
    }
    if (numeric <= targetHours) {
        return 1;
    }
    const delta = numeric - targetHours;
    if (delta <= 2) {
        return 0.7;
    }
    if (delta <= 6) {
        return 0.4;
    }
    return 0.1;
}

function calculateScores(answers: Answers) {
    const visibleQuestions = questionBank.filter((question) => isQuestionVisible(question, answers));
    const answeredQuestions = visibleQuestions.filter((question) => {
        const value = answers[question.id];
        if (question.type === 'number') {
            return value !== undefined && value !== '';
        }
        return value !== undefined;
    });

    const riskCounts: Record<RiskLevel, number> = {
        critical: 0,
        high: 0,
        medium: 0,
    };

    answeredQuestions.forEach((question) => {
        riskCounts[question.risk] += 1;
    });

    const activeRiskWeightSum = (Object.keys(riskCounts) as RiskLevel[]).reduce((sum, risk) => {
        return sum + (riskCounts[risk] > 0 ? riskWeights[risk] : 0);
    }, 0);

    const questionWeights = new Map<QuestionId, number>();
    answeredQuestions.forEach((question) => {
        const count = riskCounts[question.risk];
        if (count === 0 || activeRiskWeightSum === 0) {
            return;
        }
        const base = riskWeights[question.risk];
        questionWeights.set(question.id, (base / count) / activeRiskWeightSum);
    });

    const questionScores = new Map<QuestionId, number>();

    answeredQuestions.forEach((question) => {
        if (question.type === 'number') {
            const score = computeNumberScore(question, answers);
            if (score !== null && score !== undefined) {
                questionScores.set(question.id, score);
            }
        } else {
            const selectedValue = answers[question.id];
            const score = getOptionScore(question, selectedValue);
            if (score !== null && score !== undefined) {
                questionScores.set(question.id, score);
            }
        }
    });

    const totalScore = Array.from(questionScores.entries()).reduce((sum, [id, score]) => {
        const weight = questionWeights.get(id) ?? 0;
        return sum + score * weight;
    }, 0);

    const moduleScores: ModuleScore[] = moduleOrder.map((moduleId) => {
        const moduleQuestions = answeredQuestions.filter((question) => question.module === moduleId);
        if (moduleQuestions.length === 0) {
            return {
                id: moduleId,
                score: null,
                label: moduleMeta[moduleId].title,
            };
        }
        const moduleRiskCounts: Record<RiskLevel, number> = {
            critical: 0,
            high: 0,
            medium: 0,
        };
        moduleQuestions.forEach((question) => {
            moduleRiskCounts[question.risk] += 1;
        });
        const moduleRiskWeightSum = (Object.keys(moduleRiskCounts) as RiskLevel[]).reduce((sum, risk) => {
            return sum + (moduleRiskCounts[risk] > 0 ? riskWeights[risk] : 0);
        }, 0);
        let moduleScore = 0;
        moduleQuestions.forEach((question) => {
            const score = questionScores.get(question.id);
            if (score === undefined) {
                return;
            }
            const count = moduleRiskCounts[question.risk];
            if (count === 0 || moduleRiskWeightSum === 0) {
                return;
            }
            const weight = (riskWeights[question.risk] / count) / moduleRiskWeightSum;
            moduleScore += score * weight;
        });
        return {
            id: moduleId,
            score: Math.round(moduleScore * 100),
            label: moduleMeta[moduleId].title,
        };
    });

    const criticalAlerts: Alert[] = [];
    answeredQuestions.forEach((question) => {
        if (!question.criticalMessage) {
            return;
        }
        const score = questionScores.get(question.id);
        if (score === undefined) {
            return;
        }
        if (score < 0.5) {
            criticalAlerts.push({
                id: question.id,
                message: question.criticalMessage,
                severity: question.risk,
            });
        }
    });

    const remediation: RemediationItem[] = answeredQuestions
        .filter((question) => question.remediation)
        .map((question) => {
            const score = questionScores.get(question.id) ?? 0;
            return {
                questionId: question.id,
                severity: question.risk,
                score,
                title: question.remediation!.title,
                action: question.remediation!.action,
                link: question.remediation!.link,
            };
        })
        .filter((item) => item.score < 0.8)
        .sort((a, b) => {
            if (a.severity === b.severity) {
                return a.score - b.score;
            }
            const order: RiskLevel[] = ['critical', 'high', 'medium'];
            return order.indexOf(a.severity) - order.indexOf(b.severity);
        })
        .slice(0, 5);

    const totalScorePercent = Math.round(totalScore * 100);
    const maturity = maturityBands.find((band) => totalScorePercent >= band.min && totalScorePercent <= band.max) ?? maturityBands[0];

    const rtoTargetOption = questionBank.find((q) => q.id === 'rtoTarget')?.options?.find((option) => option.value === String(answers.rtoTarget));
    const targetHours = rtoTargetOption?.hours;
    const lastTestValue = answers.lastTestRto;
    const actualHoursValue = typeof lastTestValue === 'number' ? lastTestValue : lastTestValue ? parseFloat(String(lastTestValue)) : undefined;
    const rtoDelta = targetHours !== undefined && actualHoursValue !== undefined && !Number.isNaN(actualHoursValue)
        ? actualHoursValue - targetHours
        : undefined;

    return {
        totalScore: totalScorePercent,
        moduleScores,
        criticalAlerts,
        remediation,
        maturity,
        targetHours,
        actualHours: actualHoursValue,
        rtoDelta: rtoDelta !== undefined ? Math.max(rtoDelta, 0) : undefined,
    };
}

export type IRRAResult = ReturnType<typeof calculateScores>;

// Use shared score color utility
const formatScoreColor = getScoreColorClasses;

export type IRRAInteractionPayload = {
    result: IRRAResult;
    questionId?: QuestionId;
    trackingContext?: string;
};

export type IRRAEmbedCtaRender = (helpers: { onClick: () => void; result: IRRAResult; className: string }) => ReactNode;

type RelatedAssessment = {
    name: string;
    href: string;
    description: string;
    icon: string;
};

const relatedAssessments: RelatedAssessment[] = [
    {
        name: 'Cybersecurity Maturity Assessment',
        href: '/tools/assessment/cybersecurity-maturity-assessment',
        description: 'Measure your overall security program maturity across all domains.',
        icon: '🛡️',
    },
    {
        name: 'Compliance Readiness Checklist',
        href: '/tools/compliance/compliance-checklist',
        description: 'Preparing for HIPAA, SOC 2, or PCI-DSS? Check your compliance readiness.',
        icon: '✅',
    },
    {
        name: 'Cloud Security Self-Assessment',
        href: '/tools/assessment/cloud-security-self-assessment',
        description: 'Assess your cloud infrastructure security for AWS, Azure, or GCP.',
        icon: '☁️',
    },
];

function RelatedAssessmentsSection() {
    return (
        <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-8">
            <div className="text-center mb-6">
                <h3 className="text-2xl font-semibold text-slate-900">Related Assessments</h3>
                <p className="mt-2 text-sm text-slate-600">
                    Expand your security posture evaluation with these complementary assessments
                </p>
            </div>
            <div className="grid gap-5 md:grid-cols-3">
                {relatedAssessments.map((assessment) => (
                    <a
                        key={assessment.href}
                        href={assessment.href}
                        className="group flex flex-col rounded-2xl border border-slate-200 bg-white dark:bg-gray-800 p-6 transition-all hover:border-[#2813e8] hover:shadow-lg"
                    >
                        <div className="text-3xl mb-3">{assessment.icon}</div>
                        <h4 className="text-lg font-semibold text-slate-900 mb-2 group-hover:text-[#2813e8]">
                            {assessment.name}
                        </h4>
                        <p className="text-sm text-slate-600 mb-4 flex-1">
                            {assessment.description}
                        </p>
                        <div className="flex items-center gap-2 text-sm font-semibold text-[#2813e8]">
                            Take Assessment
                            <svg
                                className="h-4 w-4 transition-transform group-hover:translate-x-1"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                        </div>
                    </a>
                ))}
            </div>
        </div>
    );
}

export type InteractiveRansomwareResilienceAssessmentProps = {
    className?: string;
    ctaHref?: string;
    ctaLabel?: string;
    ctaSlot?: IRRAEmbedCtaRender;
    showCta?: boolean;
    trackingContext?: string;
    onComplete?(result: IRRAResult): void;
    onRemediationClick?(payload: IRRAInteractionPayload): void;
    onCtaClick?(payload: IRRAInteractionPayload): void;
};

export function InteractiveRansomwareResilienceAssessment({
    className,
    ctaHref,
    ctaLabel,
    ctaSlot,
    showCta = true,
    trackingContext,
    onComplete,
    onRemediationClick,
    onCtaClick,
}: InteractiveRansomwareResilienceAssessmentProps = {}) {
    const storageKey = useMemo(() => (trackingContext ? `irra-progress:${trackingContext}` : 'irra-progress'), [trackingContext]);
    const isRestoringRef = useRef(true);

    const [answers, setAnswers] = useState<Answers>(initialAnswers);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [numberDraft, setNumberDraft] = useState('');
    const [showResults, setShowResults] = useState(false);
    const [hasReportedResults, setHasReportedResults] = useState(false);
    const [pdfPending, setPdfPending] = useState(false);
    const [pdfError, setPdfError] = useState<string | null>(null);

    useEffect(() => {
        isRestoringRef.current = true;
        if (typeof window === 'undefined') {
            isRestoringRef.current = false;
            return;
        }

        // Reset to initial state
        setAnswers(initialAnswers());
        setCurrentIndex(0);
        setNumberDraft('');
        setShowResults(false);
        setHasReportedResults(false);

        // Load saved progress using shared utility
        const result = loadAssessmentProgress<IRRASavedProgress>(storageKey, '1.0', 30);

        if (result.success && result.data) {
            const progress = result.data;

            // Parse and validate answers
            const parsedAnswers: Answers = {};
            if (progress.answers && typeof progress.answers === 'object') {
                questionBank.forEach((question) => {
                    const value = progress.answers[question.id];
                    if (value === undefined || value === null) {
                        return;
                    }
                    if (question.type === 'number') {
                        const numeric = typeof value === 'number' ? value : Number(value);
                        if (!Number.isNaN(numeric)) {
                            parsedAnswers[question.id] = numeric;
                        }
                    } else if (typeof value === 'string') {
                        parsedAnswers[question.id] = value;
                    }
                });
            }

            setAnswers(parsedAnswers);
            setShowResults(Boolean(progress.showResults));

            if (typeof progress.currentIndex === 'number' && Number.isFinite(progress.currentIndex)) {
                setCurrentIndex(Math.max(0, Math.floor(progress.currentIndex)));
            }

            if (typeof progress.numberDraft === 'string') {
                setNumberDraft(progress.numberDraft);
            }
        }

        setHasReportedResults(false);
        isRestoringRef.current = false;
    }, [storageKey]);

    const questionSequence = useMemo(() => buildQuestionSequence(answers), [answers]);
    const totalQuestions = questionSequence.length;
    const currentQuestion = !showResults ? questionSequence[currentIndex] : undefined;
    const currentQuestionType = currentQuestion?.type ?? null;

    useEffect(() => {
        if (isRestoringRef.current) {
            return;
        }
        if (totalQuestions === 0) {
            if (currentIndex !== 0) {
                setCurrentIndex(0);
            }
            return;
        }
        if (!showResults && currentIndex > totalQuestions - 1) {
            setCurrentIndex(totalQuestions - 1);
        }
    }, [totalQuestions, currentIndex, showResults]);

    useEffect(() => {
        if (isRestoringRef.current) {
            return;
        }
        if (typeof window === 'undefined') {
            return;
        }

        // Prepare progress data
        const progressData: Partial<IRRASavedProgress> = {
            version: '1.0',
            answers,
            currentIndex,
            showResults,
        };

        // Include numberDraft if we're on a number question
        if (!showResults && currentQuestionType === 'number') {
            progressData.numberDraft = numberDraft;
        }

        // Save using shared utility
        saveAssessmentProgress(storageKey, progressData);
    }, [answers, currentIndex, showResults, numberDraft, storageKey, currentQuestionType]);

    const activeModules = useMemo(() => {
        const seen = new Set<ModuleId>();
        const ordered: ModuleId[] = [];
        questionSequence.forEach((question) => {
            if (!seen.has(question.module)) {
                seen.add(question.module);
                ordered.push(question.module);
            }
        });
        return ordered.length > 0 ? ordered : moduleOrder;
    }, [questionSequence]);

    const totalModules = activeModules.length;
    const currentModuleIndex = currentQuestion ? Math.max(activeModules.indexOf(currentQuestion.module), 0) : totalModules - 1;
    const modulePositionDisplay =
        totalModules === 0 ? 0 : showResults ? totalModules : Math.max(currentModuleIndex + 1, 1);
    const moduleProgressPercent = totalModules === 0 ? 0 : Math.min(100, (modulePositionDisplay / totalModules) * 100);

    const answeredCount = useMemo(
        () => questionSequence.filter((question) => isQuestionAnswered(question, answers)).length,
        [questionSequence, answers],
    );
    const questionProgressPercent = showResults
        ? 100
        : totalQuestions === 0
          ? 0
          : Math.min(100, (answeredCount / totalQuestions) * 100);
    const currentQuestionNumber =
        totalQuestions === 0 ? 0 : showResults ? totalQuestions : Math.max(currentIndex + 1, 1);

    const result = useMemo(() => calculateScores(answers), [answers]);
    const { totalScore, moduleScores, criticalAlerts, remediation, maturity, rtoDelta, targetHours, actualHours } = result;
    const maturityLabel = maturity.label;
    const resolvedCtaHref = ctaHref ?? '/contact';
    const resolvedCtaLabel = ctaLabel ?? 'Book Your Deep-Dive Strategy Call';

    useEffect(() => {
        if (!showResults || hasReportedResults) {
            return;
        }
        if (typeof window === 'undefined') {
            return;
        }
        onComplete?.(result);
        const win = window as typeof window & { dataLayer?: Array<Record<string, unknown>> };
        win.dataLayer = win.dataLayer || [];
        const payload: Record<string, unknown> = {
            event: 'irra_results',
            maturityScore: totalScore,
            maturityLevel: maturityLabel,
            rtoDelta: rtoDelta ?? 0,
        };
        if (trackingContext) {
            payload.trackingContext = trackingContext;
        }
        win.dataLayer.push(payload);
        setHasReportedResults(true);
    }, [showResults, hasReportedResults, totalScore, maturityLabel, rtoDelta, trackingContext, onComplete, result]);

    useEffect(() => {
        if (currentQuestion?.type === 'number') {
            const existing = answers[currentQuestion.id];
            if (typeof existing === 'number' && !Number.isNaN(existing)) {
                setNumberDraft(String(existing));
            } else {
                setNumberDraft('');
            }
        } else {
            setNumberDraft('');
        }
    }, [currentQuestion, answers]);

    const commitAnswer = useCallback(
        (question: Question, rawValue: string | number) => {
            const updatedAnswers: Answers = { ...answers, [question.id]: rawValue };
            let sequence = buildQuestionSequence(updatedAnswers);
            const validIds = new Set(sequence.map((item) => item.id));
            (Object.keys(updatedAnswers) as QuestionId[]).forEach((key) => {
                if (!validIds.has(key)) {
                    delete updatedAnswers[key];
                }
            });
            sequence = buildQuestionSequence(updatedAnswers);
            const sequenceLength = sequence.length;

            let nextIndex = currentIndex;
            let shouldShowResults = false;

            if (sequenceLength === 0) {
                shouldShowResults = true;
                nextIndex = 0;
            } else {
                const currentPos = sequence.findIndex((item) => item.id === question.id);
                if (currentPos === -1) {
                    nextIndex = Math.min(currentIndex, sequenceLength - 1);
                } else if (currentPos + 1 >= sequenceLength) {
                    shouldShowResults = true;
                    nextIndex = sequenceLength - 1;
                } else {
                    nextIndex = currentPos + 1;
                }
            }

            setAnswers(updatedAnswers);
            setNumberDraft('');
            if (shouldShowResults) {
                setHasReportedResults(false);
                setShowResults(true);
                setCurrentIndex(nextIndex);
            } else {
                setShowResults(false);
                setCurrentIndex(nextIndex);
            }
        },
        [answers, currentIndex],
    );

    const handleReset = useCallback(() => {
        setAnswers(initialAnswers());
        setCurrentIndex(0);
        setNumberDraft('');
        setShowResults(false);
        setHasReportedResults(false);

        // Clear saved progress using shared utility
        clearAssessmentProgress(storageKey);
    }, [storageKey]);

    const handleBack = useCallback(() => {
        setShowResults(false);
        setHasReportedResults(false);
        setNumberDraft('');
        setCurrentIndex((prev) => Math.max(prev - 1, 0));
    }, []);

    const handleOptionSelect = useCallback(
        (value: string) => {
            if (!currentQuestion || currentQuestion.type !== 'single') {
                return;
            }
            const questionRef = currentQuestion;
            setTimeout(() => commitAnswer(questionRef, value), 160);
        },
        [currentQuestion, commitAnswer],
    );

    const attemptNumberCommit = useCallback(() => {
        if (!currentQuestion || currentQuestion.type !== 'number') {
            return;
        }
        const trimmed = numberDraft.trim();
        if (!trimmed) {
            return;
        }
        const numeric = Number(trimmed);
        if (!Number.isFinite(numeric)) {
            return;
        }
        const min = currentQuestion.numberConfig?.min ?? 0;
        const max = currentQuestion.numberConfig?.max ?? 168;
        const clamped = Math.min(Math.max(numeric, min), max);
        commitAnswer(currentQuestion, clamped);
    }, [currentQuestion, numberDraft, commitAnswer]);

    const handleRemediationClick = useCallback(
        (questionId: QuestionId) => {
            onRemediationClick?.({
                result,
                questionId,
                trackingContext,
            });
            if (typeof window === 'undefined') {
                return;
            }
            const win = window as typeof window & { dataLayer?: Array<Record<string, unknown>> };
            win.dataLayer = win.dataLayer || [];
            const payload: Record<string, unknown> = {
                event: 'irra_remediation_click',
                questionId,
                maturityScore: totalScore,
                maturityLevel: maturityLabel,
            };
            if (trackingContext) {
                payload.trackingContext = trackingContext;
            }
            win.dataLayer.push(payload);
        },
        [onRemediationClick, result, trackingContext, totalScore, maturityLabel],
    );

    const handleCtaClick = useCallback(() => {
        onCtaClick?.({
            result,
            trackingContext,
        });
        if (typeof window === 'undefined') {
            return;
        }
        const win = window as typeof window & { dataLayer?: Array<Record<string, unknown>> };
        win.dataLayer = win.dataLayer || [];
        const payload: Record<string, unknown> = {
            event: 'irra_cta_click',
            maturityScore: totalScore,
            maturityLevel: maturityLabel,
        };
        if (trackingContext) {
            payload.trackingContext = trackingContext;
        }
        win.dataLayer.push(payload);
    }, [onCtaClick, result, trackingContext, totalScore, maturityLabel]);

    /**
     * `generateIRRAPDF` is async now: jsPDF is fetched at runtime, from the
     * global DynamicToolLoader injects in a blog embed or from the pinned
     * jsDelivr build on `/tools/{slug}`. The promise is awaited and its
     * rejection surfaced — never `void`-ed — so a CDN failure cannot leave the
     * button stuck on "Generating…" with nothing downloaded.
     */
    const handleExportPDF = useCallback(async () => {
        // Track PDF export
        if (typeof window !== 'undefined') {
            const win = window as typeof window & { dataLayer?: Array<Record<string, unknown>> };
            win.dataLayer = win.dataLayer || [];
            const payload: Record<string, unknown> = {
                event: 'irra_pdf_export',
                maturityScore: totalScore,
                maturityLevel: maturityLabel,
            };
            if (trackingContext) {
                payload.trackingContext = trackingContext;
            }
            win.dataLayer.push(payload);
        }

        // Generate and download PDF
        setPdfError(null);
        setPdfPending(true);
        try {
            await generateIRRAPDF(result);
        } catch {
            setPdfError(
                'The PDF library could not be loaded, so no report was created. Check your connection and try again.',
            );
        } finally {
            setPdfPending(false);
        }
    }, [result, totalScore, maturityLabel, trackingContext]);

    if (showResults) {
        return (
            <div className={clsx('space-y-8', className)}>
                <div className="rounded-3xl border border-slate-200 bg-white dark:bg-gray-800 shadow-lg p-8 md:p-12">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                        <div className="flex-1">
                            <span className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
                                IRRA Scorecard
                                <span className="h-1 w-8 rounded-full bg-[#2813e8]"></span>
                            </span>
                            <h2 className="mt-4 text-3xl md:text-4xl font-bold text-slate-900">
                                Recovery Maturity Score: <span className="text-[#2813e8]">{totalScore}%</span>
                            </h2>
                            <p className="mt-3 text-lg text-slate-600">
                                {maturity.label} — {maturity.description}
                            </p>
                            <button
                                type="button"
                                onClick={handleExportPDF}
                                disabled={pdfPending}
                                aria-busy={pdfPending}
                                className="mt-4 inline-flex items-center gap-2 rounded-xl border border-[#2813e8] bg-white dark:bg-gray-800 px-4 py-2 text-sm font-semibold text-[#2813e8] transition hover:bg-[#2813e8]/10 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                {pdfPending ? 'Generating PDF\u2026' : 'Export PDF Report'}
                            </button>
                            {pdfError && (
                                <div
                                    role="alert"
                                    className="mt-3 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800"
                                >
                                    {pdfError}
                                </div>
                            )}
                        </div>
                        <div className="flex flex-col items-center justify-center rounded-2xl border border-[#2813e8]/20 bg-[#2813e8]/5 px-8 py-6 text-center">
                            <div className="text-5xl font-black text-[#2813e8]">{totalScore}%</div>
                            <div className="mt-2 text-sm font-semibold uppercase tracking-wide text-[#2813e8]">
                                Recovery Maturity
                            </div>
                        </div>
                    </div>
                    <div className="mt-10 grid gap-6 md:grid-cols-2">
                        {moduleScores.map((module) => (
                            <div
                                key={module.id}
                                className={clsx(
                                    'rounded-2xl border p-6 transition-shadow',
                                    formatScoreColor(module.score),
                                    module.score !== null && 'shadow-sm',
                                )}
                            >
                                <div className="flex items-start justify-between">
                                    <div>
                                        <span className="text-2xl">{moduleMeta[module.id].icon}</span>
                                        <h3 className="mt-3 text-lg font-semibold">{module.label}</h3>
                                    </div>
                                    <div className="text-3xl font-bold">
                                        {module.score !== null ? `${module.score}%` : '—'}
                                    </div>
                                </div>
                                <p className="mt-4 text-sm text-slate-600">
                                    {module.score === null
                                        ? 'Skipped — not enough context to score this pillar.'
                                        : module.score >= 75
                                          ? 'Green: Validated readiness with strong controls.'
                                          : module.score >= 45
                                            ? 'Amber: Controls exist but gaps threaten recovery confidence.'
                                            : 'Red: Immediate attention required to close systemic failure points.'}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>

                {(criticalAlerts.length > 0 || (rtoDelta !== undefined && rtoDelta > 0)) && (
                    <div className="rounded-3xl border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/40 p-8">
                        <h3 className="text-xl font-semibold text-rose-700 dark:text-rose-300">Critical Risk Alerts</h3>
                        <ul className="mt-4 space-y-4 text-sm text-rose-800 dark:text-rose-300">
                            {criticalAlerts.map((alert) => (
                                <li key={alert.id} className="flex items-start gap-2">
                                    <span className="mt-1 text-lg">⚠️</span>
                                    <span>{alert.message}</span>
                                </li>
                            ))}
                            {rtoDelta !== undefined && rtoDelta > 0 && targetHours !== undefined && actualHours !== undefined && (
                                <li className="flex items-start gap-2">
                                    <span className="mt-1 text-lg">⏳</span>
                                    <span>
                                        RTO Delta Gap: Your tested recovery time is{' '}
                                        <strong>{actualHours.toFixed(1)} hours</strong>, exceeding the business target of{' '}
                                        <strong>{targetHours.toFixed(1)} hours</strong> by{' '}
                                        <strong>{rtoDelta.toFixed(1)} hours</strong>. This is a critical failure of assurance and
                                        should be remediated before the next audit.
                                    </span>
                                </li>
                            )}
                        </ul>
                    </div>
                )}

                <div className="rounded-3xl border border-slate-200 bg-white dark:bg-gray-800 p-8">
                    <div className="flex items-center justify-between gap-4">
                        <h3 className="text-xl font-semibold text-slate-900">Personalized Recovery Roadmap</h3>
                        <span className="rounded-full bg-[#2813e8]/10 px-4 py-1 text-sm font-semibold text-[#2813e8]">
                            Top {remediation.length || 3} Priorities
                        </span>
                    </div>
                    {remediation.length === 0 ? (
                        <p className="mt-4 text-sm text-slate-600">
                            No critical weaknesses detected. Keep validating your playbooks and expanding automation to remain resilient.
                        </p>
                    ) : (
                        <ol className="mt-6 space-y-5">
                            {remediation.map((item, index) => (
                                <li key={item.questionId} className="rounded-2xl border border-slate-200 p-5">
                                    <div className="flex flex-wrap items-center justify-between gap-3">
                                        <div className="flex items-center gap-3">
                                            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#2813e8]/10 text-[#2813e8] font-semibold">
                                                {index + 1}
                                            </span>
                                            <h4 className="text-lg font-semibold text-slate-900">{item.title}</h4>
                                        </div>
                                        <span
                                            className={clsx(
                                                'rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide',
                                                item.severity === 'critical'
                                                    ? 'bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300'
                                                    : item.severity === 'high'
                                                      ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
                                                      : 'bg-slate-100 text-slate-700',
                                            )}
                                        >
                                            {item.severity}
                                        </span>
                                    </div>
                                    <p className="mt-3 text-sm text-slate-600">{item.action}</p>
                                    <a
                                        href={item.link.href}
                                        onClick={() => handleRemediationClick(item.questionId)}
                                        className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#2813e8] hover:text-[#1d0dbd]"
                                    >
                                        {item.link.label}
                                        <svg
                                            className="h-4 w-4"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 7l-10 10m0 0h7m-7 0v-7" />
                                        </svg>
                                    </a>
                                </li>
                            ))}
                        </ol>
                    )}
                </div>

                {showCta !== false && (
                    <div className="rounded-3xl border border-[#2813e8]/20 bg-gradient-to-br from-[#2813e8] to-[#2cc3d1] p-10 text-white text-center">
                        <h3 className="text-3xl font-bold">Ready to Close Your Gaps?</h3>
                        <p className="mt-3 text-lg text-white/90">
                            Bring this diagnostic to our ransomware recovery architects. Walk away with a prioritized remediation roadmap and
                            investment plan.
                        </p>
                        <div className="mt-6 flex flex-wrap justify-center gap-4">
                            {ctaSlot
                                ? ctaSlot({
                                      onClick: handleCtaClick,
                                      result,
                                      className:
                                          'inline-flex items-center justify-center rounded-xl bg-white dark:bg-gray-800 px-6 py-3 text-lg font-semibold text-[#2813e8] transition hover:bg-slate-100',
                                  })
                                : (
                                      <a
                                          href={resolvedCtaHref}
                                          onClick={handleCtaClick}
                                          className="inline-flex items-center justify-center rounded-xl bg-white dark:bg-gray-800 px-6 py-3 text-lg font-semibold text-[#2813e8] transition hover:bg-slate-100"
                                      >
                                          {resolvedCtaLabel}
                                      </a>
                                  )}
                        </div>
                    </div>
                )}

                {/* Lead Capture Form */}
                <LeadCaptureForm
                    source="ransomware-resilience-assessment"
                    title="Get Expert Recovery Planning Guidance"
                    description="Our ransomware recovery architects will review your assessment results and provide a prioritized remediation roadmap with investment recommendations."
                    deliverable="a customized recovery improvement plan"
                    resultsSummary={(() => {
                        const modulesSummary = moduleScores
                            .map(m => `• ${m.label}: ${m.score !== null ? `${m.score}%` : 'Skipped'}`)
                            .join('\n');
                        const alertsSummary = criticalAlerts.length > 0
                            ? criticalAlerts.map(a => `• ${a.message}`).join('\n')
                            : 'None detected';
                        const topRemediation = remediation.slice(0, 3)
                            .map((r, i) => `${i + 1}. [${r.severity.toUpperCase()}] ${r.title}`)
                            .join('\n');
                        return `RANSOMWARE RESILIENCE ASSESSMENT RESULTS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Overall Recovery Maturity Score: ${totalScore}%
Maturity Level: ${maturity.label}
${maturity.description}

MODULE SCORES:
${modulesSummary}

CRITICAL ALERTS:
${alertsSummary}
${rtoDelta !== undefined && rtoDelta > 0 ? `\n⚠️ RTO Gap: Recovery time exceeds target by ${rtoDelta.toFixed(1)} hours` : ''}

TOP REMEDIATION PRIORITIES:
${topRemediation || 'No critical weaknesses detected'}`;
                    })()}
                />

                <RelatedAssessmentsSection />

                <div className="flex justify-center">
                    <button
                        type="button"
                        onClick={handleReset}
                        className={clsx(
                            'inline-flex items-center justify-center rounded-xl border px-6 py-3 text-lg font-semibold transition',
                            showCta !== false ? 'border-[#2813e8]/20 text-[#2813e8] hover:bg-[#2813e8]/10' : 'border-slate-200 text-slate-600 hover:bg-slate-100',
                        )}
                    >
                        Restart Assessment
                    </button>
                </div>
            </div>
        );
    }

    if (!currentQuestion) {
        return (
            <div className={clsx('rounded-3xl border border-slate-200 bg-white dark:bg-gray-800 p-10 text-center text-sm text-slate-600', className)}>
                All questions have been completed. Restart the assessment to begin again.
                <div className="mt-6">
                    <button
                        type="button"
                        onClick={handleReset}
                        className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] px-6 py-2 text-sm font-semibold text-white shadow transition hover:shadow-lg"
                    >
                        Restart Assessment
                    </button>
                </div>
            </div>
        );
    }

    const moduleBadge = moduleMeta[currentQuestion.module];

    return (
        <div className={clsx('rounded-3xl border border-slate-200 bg-white dark:bg-gray-800 shadow-lg', className)}>
            <div className="border-b border-slate-200 bg-slate-50/80 px-6 py-6 sm:px-10">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                        <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Question {Math.max(currentQuestionNumber, 1)} of {Math.max(totalQuestions, 1)}
                            <span className="h-1 w-6 rounded-full bg-[#2813e8]"></span>
                        </span>
                        <p className="mt-4 text-sm text-slate-600">
                            Diagnose your recovery posture one question at a time. Responses drive weighted scoring in real-time.
                        </p>
                    </div>
                    <span className="inline-flex items-center gap-2 rounded-full bg-white dark:bg-gray-800 px-4 py-2 text-sm font-semibold text-[#2813e8] shadow-sm">
                        <span className="text-lg leading-none">{moduleBadge.icon}</span>
                        {moduleBadge.title}
                    </span>
                </div>
                <div className="mt-6 space-y-4">
                    <div>
                        <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-slate-500">
                            <span>Pillar {modulePositionDisplay || 0} of {Math.max(totalModules, 1)}</span>
                            <span>{Math.round(moduleProgressPercent)}%</span>
                        </div>
                        <div className="mt-2 h-2 rounded-full bg-white/60 dark:bg-gray-800/60">
                            <div
                                className="h-full rounded-full bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] transition-all"
                                style={{ width: `${moduleProgressPercent}%` }}
                            />
                        </div>
                    </div>
                    <div>
                        <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-slate-500">
                            <span>Question {Math.max(currentQuestionNumber, 1)} of {Math.max(totalQuestions, 1)}</span>
                            <span>{Math.round(questionProgressPercent)}%</span>
                        </div>
                        <div className="mt-2 h-2 rounded-full bg-white/60 dark:bg-gray-800/60">
                            <div
                                className="h-full rounded-full bg-[#2813e8] transition-all"
                                style={{ width: `${questionProgressPercent}%` }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="px-6 py-10 sm:px-10 space-y-6">
                <div>
                    <h3 className="text-2xl font-semibold text-slate-900">{currentQuestion.prompt}</h3>
                    {currentQuestion.helper && <p className="mt-3 text-sm text-slate-600">{currentQuestion.helper}</p>}
                </div>
                {currentQuestion.type === 'single' && currentQuestion.options && (
                    <div className="space-y-3">
                        {currentQuestion.options.map((option) => {
                            const isSelected = answers[currentQuestion.id] === option.value;
                            return (
                                <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => handleOptionSelect(option.value)}
                                    className={clsx(
                                        'flex w-full flex-col rounded-2xl border p-4 text-left transition-all',
                                        isSelected
                                            ? 'border-[#2813e8] bg-[#2813e8]/10 shadow-md'
                                            : 'border-slate-200 hover:border-[#2813e8]/60 hover:bg-[#2813e8]/5',
                                    )}
                                >
                                    <span className="text-base font-semibold text-slate-900">{option.label}</span>
                                    {option.helper && (
                                        <span className="mt-1 text-sm font-semibold text-[#2813e8]">{option.helper}</span>
                                    )}
                                    {option.detail && <span className="mt-2 text-xs text-slate-600">{option.detail}</span>}
                                </button>
                            );
                        })}
                    </div>
                )}
                {currentQuestion.type === 'number' && (
                    <div className="space-y-5">
                        {(() => {
                            const min = currentQuestion.numberConfig?.min ?? 0;
                            const max = currentQuestion.numberConfig?.max ?? 168;
                            const step = currentQuestion.numberConfig?.step ?? 0.5;
                            const trimmedDraft = typeof numberDraft === 'string' ? numberDraft.trim() : '';
                            const rawNumeric = trimmedDraft === '' ? undefined : Number(trimmedDraft);
                            const hasValidNumber = rawNumeric !== undefined && Number.isFinite(rawNumeric);
                            const answerValue = answers[currentQuestion.id];
                            const existingAnswer =
                                typeof answerValue === 'number' && Number.isFinite(answerValue)
                                    ? Math.min(Math.max(answerValue, min), max)
                                    : undefined;
                            const sliderValue =
                                rawNumeric !== undefined && Number.isFinite(rawNumeric)
                                    ? Math.min(Math.max(rawNumeric, min), max)
                                    : existingAnswer ?? min;

                            return (
                                <>
                                    <div className="space-y-4">
                                        <div className="flex flex-col gap-4">
                                            <input
                                                type="range"
                                                aria-label={currentQuestion.prompt}
                                                min={min}
                                                max={max}
                                                step={step}
                                                value={sliderValue}
                                                onChange={(event) => {
                                                    const value = Number(event.target.value);
                                                    const clamped = Math.min(Math.max(value, min), max);
                                                    setNumberDraft(String(clamped));
                                                }}
                                                className="w-full accent-[#2813e8]"
                                            />
                                            <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-slate-500">
                                                <span>{min} hrs</span>
                                                <span>{max} hrs</span>
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                                            <div className="flex w-full items-center gap-3 rounded-2xl border border-slate-200 bg-white dark:bg-gray-800 px-4 py-3 text-lg font-semibold text-slate-900 shadow-sm sm:w-72">
                                                <input
                                                    type="number"
                                                    aria-label={currentQuestion.prompt}
                                                    inputMode="decimal"
                                                    className="w-full border-none bg-transparent text-lg font-semibold text-slate-900 focus:outline-none"
                                                    value={numberDraft}
                                                    onChange={(event) => {
                                                        const value = event.target.value;
                                                        if (value === '') {
                                                            setNumberDraft('');
                                                            return;
                                                        }
                                                        setNumberDraft(value);
                                                    }}
                                                    onBlur={(event) => {
                                                        const value = event.target.value.trim();
                                                        if (value === '') {
                                                            setNumberDraft('');
                                                            return;
                                                        }
                                                        const numeric = Number(value);
                                                        if (!Number.isFinite(numeric)) {
                                                            return;
                                                        }
                                                        const clamped = Math.min(Math.max(numeric, min), max);
                                                        setNumberDraft(String(clamped));
                                                    }}
                                                    onFocus={(event) => event.target.select()}
                                                    onKeyDown={(event) => {
                                                        if (event.key === 'Enter') {
                                                            event.preventDefault();
                                                            attemptNumberCommit();
                                                        }
                                                    }}
                                                    min={min}
                                                    max={max}
                                                    step={step}
                                                    placeholder={currentQuestion.numberConfig?.placeholder}
                                                />
                                                {currentQuestion.numberConfig?.suffix && (
                                                    <span className="text-sm font-semibold text-slate-600">
                                                        {currentQuestion.numberConfig.suffix}
                                                    </span>
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={attemptNumberCommit}
                                                className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] px-5 py-2 text-sm font-semibold text-white shadow transition hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-60"
                                                disabled={!hasValidNumber}
                                            >
                                                Record &amp; Continue
                                            </button>
                                        </div>
                                    </div>
                                    <p className="text-xs text-slate-500">
                                        Use the slider or field to capture your tested recovery time, then select Record &amp; Continue or press Enter.
                                    </p>
                                </>
                            );
                        })()}
                    </div>
                )}
            </div>

            <div className="flex flex-col gap-3 border-t border-slate-200 bg-slate-50/70 px-6 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-10">
                <div className="text-xs text-slate-500">
                    Your responses never leave this browser. Scoring runs 100% client-side for instant insights.
                </div>
                <div className="flex flex-wrap gap-3">
                    <button
                        type="button"
                        onClick={handleBack}
                        disabled={currentIndex === 0}
                        className={clsx(
                            'rounded-xl border px-5 py-2 text-sm font-semibold transition',
                            currentIndex === 0
                                ? 'cursor-not-allowed border-slate-200 bg-white dark:bg-gray-800 text-slate-300'
                                : 'border-slate-200 bg-white dark:bg-gray-800 text-slate-700 hover:border-[#2813e8]/50 hover:text-[#2813e8]',
                        )}
                    >
                        Back
                    </button>
                    <button
                        type="button"
                        onClick={handleReset}
                        className="rounded-xl border border-transparent px-5 py-2 text-sm font-semibold text-slate-500 hover:bg-white dark:hover:bg-gray-800/60"
                    >
                        Restart
                    </button>
                </div>
            </div>
        </div>
    );
}
